import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { PtinRequiredView } from '@/features/iam/providers/components/PtinRequiredView'
import { WorkspaceFilterViewHeadingContainer } from '@/components/WorkspaceFilterViewHeadingContainer'
import { useFeatures } from '@/contexts/root-store-context'
import { useDocumentTitle } from '@/hooks/document-title'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { useActivation } from '@/hooks/view-model-hooks'
import { isRefreshDesign } from '@/infra/design'
import { JobFilterViewHeadingContainer } from '@/features/jobs/list/components/JobFilterViewHeading/JobFilterViewHeadingContainer'
import { JobManagement } from '@/features/jobs/list/components/JobManagement'
import { JobsViewerContainer } from '@/features/jobs/list/components/JobManagement/JobsViewer/JobsViewerContainer'
import { type JobManagementViewModel } from '@/features/jobs/list/JobManagementViewModel'
import { JobListPageContainer } from './JobListPageContainer'

export default observer(function JobManagementContainer() {
  const { internal } = useFeatures(({ jobs }) => ({
    internal: jobs.internal(),
  }))

  return isRefreshDesign ? (
    <JobListPageContainer vm={internal.jobManagementViewModel} />
  ) : (
    <LegacyJobManagementContainer vm={internal.jobManagementViewModel} />
  )
})

interface LegacyJobManagementContainerProps {
  vm: JobManagementViewModel
}

const LegacyJobManagementContainer = observer(
  function LegacyJobManagementContainer({
    vm,
  }: LegacyJobManagementContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    useDocumentTitle('Job Management')
    useActivation(vm)
    useHotkey(['Escape'], vm.searcher.searchText.clear)

    if (vm.ptinRequired) {
      return <PtinRequiredView corporateMode={shellInfo.corporateMode} />
    }

    return (
      <JobManagement
        isInitialSearchRunning={
          vm.searcher.searchState === 'InitialSearchRunning'
        }
        heading={
          <WorkspaceFilterViewHeadingContainer
            allOptionText="All workspaces"
            primaryText="Job management"
            filter={vm.searcher.workspacesFilter}
            testId="workspace-filter-view-heading-dropdown"
          />
        }
        filterHeading={<JobFilterViewHeadingContainer vm={vm.searcher} />}
      >
        {vm.fetch.match({
          pending: () => <ContentLoading />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => <JobsViewerContainer vm={vm} />,
        })}
      </JobManagement>
    )
  },
)
